import { useCallback, useEffect, useMemo, useState } from 'react';
import type { NavView } from '../components/Layout';
import OnboardingTour, { type TourStepDef } from '../components/OnboardingTour';
import {
  type Municipio,
  type OnboardingState,
  type OnboardingStepKey,
  type OrgThresholds,
  completeOnboarding,
  completeOnboardingStep,
  createEmailConfig,
  fetchMunicipios,
  fetchOnboarding,
  getOrgSettings,
  testEmailConfig,
  testOnboardingSmtp,
  updateOnboardingMunicipios,
  updateOrgSettings,
} from '../services/api';
import { useT } from '../i18n';

// S10-T07 (US-S10-007): onboarding assistido — wizard passo a passo para
// novos tenants: e-mail (IMAP/SMTP com teste de conexao), selecao de
// municipios do catalogo, thresholds de validacao e tour guiado pela
// interface. O checklist (progresso visivel) vem de GET /api/onboarding e
// a conclusao notifica os parceiros da organizacao (backend).
//
// Guard por role feito no App.tsx (MANAGE_ROLES). O backend e fail-closed:
// passos verificaveis (email/municipios/thresholds) so concluem quando a
// configuracao real da org ja vale — este componente apenas consome.

const WIZARD_STEPS: OnboardingStepKey[] = ['email', 'municipios', 'thresholds', 'tour'];

const inputCls =
  'w-full rounded-md border border-brand-300 px-3 py-2 text-sm text-brand-800';
const btnPrimary =
  'rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50';
const btnNeutral =
  'rounded-md border border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50';

type Apply = (s: OnboardingState) => void;

interface StepProps {
  state: OnboardingState;
  apply: Apply;
  reload: () => Promise<void>;
  onNavigate: (v: NavView) => void;
}

function stepOf(state: OnboardingState, key: OnboardingStepKey) {
  return state.steps.find((s) => s.key === key);
}

// ---------------------------------------------------------------------------
// Ajuda contextual (criterio: tooltips + links para docs/secoes do produto)
// ---------------------------------------------------------------------------

function HelpBox({
  step,
  onNavigate,
}: {
  step: OnboardingStepKey;
  onNavigate: (v: NavView) => void;
}) {
  const t = useT();
  const link: { view: NavView; label: string } | null =
    step === 'email'
      ? { view: 'settings', label: t('onboarding.help.link.settingsEmail') }
      : step === 'municipios'
        // Decisao de produto: o catalogo completo e so super_user — para
        // roles de org o link aponta para "Solicitar provedor" (provedores
        // suportados + formulario), que qualquer usuario logado renderiza.
        ? { view: 'request-provider', label: t('layout.nav.requestProvider') }
        : step === 'thresholds'
          ? { view: 'settings', label: t('onboarding.help.link.settingsThresholds') }
          : null;
  return (
    <div className="rounded-md border border-brand-100 bg-brand-50 px-3 py-2 text-xs text-brand-600">
      <span className="font-semibold text-brand-700">{t('onboarding.help.title')}: </span>
      {t(`onboarding.help.${step}`)}
      {link && (
        <>
          {' '}
          <button
            type="button"
            onClick={() => onNavigate(link.view)}
            className="font-medium text-brand-700 underline hover:text-brand-900"
          >
            {link.label}
          </button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Passo 1: E-mail (IMAP de captura + SMTP de envio, com teste de conexao)
// ---------------------------------------------------------------------------

function EmailStep({ state, apply, reload, onNavigate }: StepProps) {
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [smtpTest, setSmtpTest] = useState<string | null>(null);

  const [imap, setImap] = useState({
    host: '',
    port: 993,
    username: '',
    password: '',
    folder: 'INBOX',
  });
  const [smtp, setSmtp] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_address: '',
    use_tls: true,
    enabled: true,
  });

  const stepDone = stepOf(state, 'email')?.status === 'completed';

  const handleSaveImap = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createEmailConfig({
        host: imap.host.trim(),
        port: imap.port,
        username: imap.username.trim(),
        password: imap.password,
        folder: imap.folder.trim() || 'INBOX',
        enabled: true,
      });
      let msg = t('onboarding.email.imapSaved');
      try {
        const test = await testEmailConfig(created.id);
        msg = test.success
          ? `${msg} ${t('onboarding.email.testOk', { message: test.message ?? 'OK' })}`
          : `${msg} ${t('onboarding.email.testFail', { error: test.error ?? '' })}`;
      } catch {
        // teste falhou na chamada — config salva mesmo assim
      }
      setSuccess(msg);
      setImap({ host: '', port: 993, username: '', password: '', folder: 'INBOX' });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setBusy(false);
    }
  }, [imap, reload, t]);

  const handleSaveSmtp = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    setSmtpTest(null);
    try {
      const payload: Record<string, unknown> = {
        host: smtp.host.trim() || null,
        port: smtp.port,
        username: smtp.username.trim() || null,
        from_address: smtp.from_address.trim() || null,
        use_tls: smtp.use_tls,
        enabled: smtp.enabled,
      };
      if (smtp.password) payload.password = smtp.password;
      await updateOrgSettings({ smtp: payload });
      setSuccess(t('onboarding.email.smtpSaved'));
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setBusy(false);
    }
  }, [smtp, reload, t]);

  const handleTestSmtp = useCallback(async () => {
    setBusy(true);
    setSmtpTest(null);
    try {
      const res = await testOnboardingSmtp();
      setSmtpTest(
        res.success
          ? t('onboarding.email.testOk', { message: res.message ?? 'OK' })
          : t('onboarding.email.testFail', { error: res.error ?? '' }),
      );
    } catch (err) {
      setSmtpTest(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setBusy(false);
    }
  }, [t]);

  const handleComplete = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      apply(await completeOnboardingStep('email'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setBusy(false);
    }
  }, [apply, t]);

  const canImap =
    imap.host.trim().length > 0 && imap.username.trim().length > 0 && imap.password.length > 0;
  const canSmtp = smtp.host.trim().length > 0;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-brand-900">{t('onboarding.email.title')}</h2>
        <p className="text-sm text-brand-600">{t('onboarding.email.subtitle')}</p>
      </div>

      <HelpBox step="email" onNavigate={onNavigate} />

      {/* Estado detectado na org */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span
          className={`rounded px-2 py-1 font-medium ${
            state.email.imap_configured
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {t('onboarding.email.imapDetected')}
        </span>
        <span
          className={`rounded px-2 py-1 font-medium ${
            state.email.smtp_configured
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {t('onboarding.email.smtpDetected')}
        </span>
      </div>

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && (
        <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>
      )}

      {/* IMAP — captura */}
      <fieldset className="rounded-md border border-brand-200 p-4">
        <legend className="px-1 text-sm font-semibold text-brand-800">
          {t('onboarding.email.imapLegend')}
        </legend>
        <p className="mb-3 text-xs text-brand-500">{t('onboarding.email.imapHint')}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label
              className="mb-1 block text-xs text-brand-500"
              title={t('onboarding.email.tip.host')}
            >
              {t('settings.email.col.host')}
            </label>
            <input
              type="text"
              value={imap.host}
              onChange={(e) => setImap({ ...imap, host: e.target.value })}
              placeholder="imap.exemplo.com"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.port')}
            </label>
            <input
              type="number"
              value={imap.port}
              onChange={(e) => setImap({ ...imap, port: Number(e.target.value) })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.user')}
            </label>
            <input
              type="text"
              value={imap.username}
              onChange={(e) => setImap({ ...imap, username: e.target.value })}
              placeholder="notas@empresa.com"
              className={inputCls}
            />
          </div>
          <div>
            <label
              className="mb-1 block text-xs text-brand-500"
              title={t('onboarding.email.tip.password')}
            >
              {t('settings.email.col.password')}
            </label>
            <input
              type="password"
              value={imap.password}
              onChange={(e) => setImap({ ...imap, password: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>
        <div className="mt-3">
          <button
            type="button"
            onClick={handleSaveImap}
            disabled={busy || !canImap}
            className={btnPrimary}
          >
            {busy ? t('settings.saving') : t('onboarding.email.saveImap')}
          </button>
        </div>
      </fieldset>

      {/* SMTP — envio */}
      <fieldset className="rounded-md border border-brand-200 p-4">
        <legend className="px-1 text-sm font-semibold text-brand-800">
          {t('onboarding.email.smtpLegend')}
        </legend>
        <p className="mb-3 text-xs text-brand-500">{t('onboarding.email.smtpHint')}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.host')}
            </label>
            <input
              type="text"
              value={smtp.host}
              onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
              placeholder="smtp.exemplo.com"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.port')}
            </label>
            <input
              type="number"
              value={smtp.port}
              onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.user')}
            </label>
            <input
              type="text"
              value={smtp.username}
              onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.password')}
            </label>
            <input
              type="password"
              value={smtp.password}
              onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.from')}
            </label>
            <input
              type="email"
              value={smtp.from_address}
              onChange={(e) => setSmtp({ ...smtp, from_address: e.target.value })}
              placeholder="no-reply@empresa.com"
              className={inputCls}
            />
          </div>
          <div className="flex items-end gap-4 pb-1">
            <label className="flex items-center gap-2 text-sm text-brand-700">
              <input
                type="checkbox"
                checked={smtp.use_tls}
                onChange={(e) => setSmtp({ ...smtp, use_tls: e.target.checked })}
                className="h-4 w-4 rounded border-brand-300 text-brand-600"
              />
              {t('settings.email.useTls')}
            </label>
            <label className="flex items-center gap-2 text-sm text-brand-700">
              <input
                type="checkbox"
                checked={smtp.enabled}
                onChange={(e) => setSmtp({ ...smtp, enabled: e.target.checked })}
                className="h-4 w-4 rounded border-brand-300 text-brand-600"
              />
              {t('settings.email.enabled')}
            </label>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleSaveSmtp}
            disabled={busy || !canSmtp}
            className={btnPrimary}
          >
            {busy ? t('settings.saving') : t('onboarding.email.saveSmtp')}
          </button>
          <button
            type="button"
            onClick={handleTestSmtp}
            disabled={busy || !state.email.smtp_configured}
            className={btnNeutral}
            title={t('onboarding.email.tip.testSmtp')}
          >
            {t('onboarding.email.testSmtp')}
          </button>
        </div>
        {smtpTest && <p className="mt-2 text-xs text-brand-600">{smtpTest}</p>}
      </fieldset>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleComplete}
          disabled={busy || stepDone || !state.email.configured}
          className={btnPrimary}
          title={t('onboarding.step.completeHint')}
        >
          {stepDone ? t('onboarding.step.done') : t('onboarding.step.complete')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Passo 2: Municipios (selecao do catalogo municipio -> provedor)
// ---------------------------------------------------------------------------

function MunicipiosStep({ state, apply, onNavigate }: StepProps) {
  const t = useT();
  const [busca, setBusca] = useState('');
  const [results, setResults] = useState<Municipio[]>([]);
  const [selected, setSelected] = useState<string[]>(state.municipios);
  const [names, setNames] = useState<Record<string, string>>({});
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const stepDone = stepOf(state, 'municipios')?.status === 'completed';

  useEffect(() => {
    setSelected(state.municipios);
  }, [state.municipios]);

  const handleSearch = useCallback(async () => {
    setSearching(true);
    setError(null);
    try {
      const res = await fetchMunicipios(busca.trim() ? { busca: busca.trim() } : undefined);
      setResults(res.slice(0, 20));
      setNames((prev) => {
        const next = { ...prev };
        for (const m of res) next[m.codigo_ibge] = `${m.nome} (${m.uf})`;
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setSearching(false);
    }
  }, [busca, t]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback((m: Municipio) => {
    setNames((prev) => ({ ...prev, [m.codigo_ibge]: `${m.nome} (${m.uf})` }));
    setSelected((prev) =>
      prev.includes(m.codigo_ibge)
        ? prev.filter((c) => c !== m.codigo_ibge)
        : [...prev, m.codigo_ibge],
    );
  }, []);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      apply(await updateOnboardingMunicipios(selected));
      setSuccess(t('onboarding.municipios.saved', { count: selected.length }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setBusy(false);
    }
  }, [apply, selected, t]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-brand-900">
          {t('onboarding.municipios.title')}
        </h2>
        <p className="text-sm text-brand-600">{t('onboarding.municipios.subtitle')}</p>
      </div>

      <HelpBox step="municipios" onNavigate={onNavigate} />

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && (
        <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder={t('onboarding.municipios.searchPlaceholder')}
          className={inputCls}
        />
        <button type="button" onClick={handleSearch} disabled={searching} className={btnNeutral}>
          {searching ? t('onboarding.municipios.searching') : t('onboarding.municipios.search')}
        </button>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-3 py-1 text-xs font-medium text-brand-800"
            >
              {names[code] ?? code}
              <button
                type="button"
                onClick={() => setSelected((prev) => prev.filter((c) => c !== code))}
                className="text-brand-500 hover:text-brand-800"
                aria-label={t('onboarding.municipios.remove')}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="max-h-64 overflow-y-auto rounded-md border border-brand-200">
        {results.length === 0 ? (
          <p className="px-3 py-4 text-sm text-brand-500">
            {t('onboarding.municipios.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-brand-100">
            {results.map((m) => {
              const isSel = selected.includes(m.codigo_ibge);
              return (
                <li
                  key={m.codigo_ibge}
                  className={`flex cursor-pointer items-center justify-between px-3 py-2 text-sm hover:bg-brand-50 ${
                    isSel ? 'bg-brand-50' : ''
                  }`}
                  onClick={() => toggle(m)}
                >
                  <span className="text-brand-800">
                    {m.nome} <span className="text-brand-500">({m.uf})</span>
                  </span>
                  <span className="flex items-center gap-3 text-xs text-brand-500">
                    <span>{m.provedor}</span>
                    <span
                      className={`rounded px-2 py-0.5 font-semibold ${
                        isSel ? 'bg-brand-700 text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {isSel
                        ? t('onboarding.municipios.selected')
                        : t('onboarding.municipios.add')}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-brand-500">
          {t('onboarding.municipios.count', { count: selected.length })}
        </p>
        <button type="button" onClick={handleSave} disabled={busy} className={btnPrimary}>
          {busy
            ? t('settings.saving')
            : stepDone
              ? t('onboarding.municipios.update')
              : t('onboarding.municipios.save')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Passo 3: Thresholds de validacao
// ---------------------------------------------------------------------------

function ThresholdsStep({ apply, reload, onNavigate }: StepProps) {
  const t = useT();
  const [values, setValues] = useState<OrgThresholds>({
    vlm_confidence: 0.7,
    auto_approve: 0.9,
    needs_review: 0.6,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    getOrgSettings()
      .then((s) => setValues(s.thresholds))
      .catch(() => {
        // defaults locais ja cobrem — nao bloqueia o wizard
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await updateOrgSettings({ thresholds: values });
      // A secao thresholds no org_config ja satisfaz o passo
      // (auto-deteccao); marcamos explicitamente para registrar
      // source="wizard" na trilha do onboarding.
      apply(await completeOnboardingStep('thresholds'));
      setSuccess(t('onboarding.thresholds.saved'));
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
      await reload().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }, [apply, reload, values, t]);

  const fields: { key: keyof OrgThresholds; label: string; hint: string }[] = [
    {
      key: 'vlm_confidence',
      label: t('settings.thresholds.vlm'),
      hint: t('settings.thresholds.vlmHint'),
    },
    {
      key: 'auto_approve',
      label: t('settings.thresholds.autoApprove'),
      hint: t('settings.thresholds.autoApproveHint'),
    },
    {
      key: 'needs_review',
      label: t('settings.thresholds.needsReview'),
      hint: t('settings.thresholds.needsReviewHint'),
    },
  ];

  const valid = fields.every((f) => values[f.key] >= 0 && values[f.key] <= 1);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-brand-900">
          {t('onboarding.thresholds.title')}
        </h2>
        <p className="text-sm text-brand-600">{t('onboarding.thresholds.subtitle')}</p>
      </div>

      <HelpBox step="thresholds" onNavigate={onNavigate} />

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && (
        <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>
      )}

      {loading ? (
        <p className="text-sm text-brand-600">{t('settings.loading')}</p>
      ) : (
        <div className="max-w-md space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-xs text-brand-500" title={f.hint}>
                {f.label}
              </label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={values[f.key]}
                onChange={(e) => setValues({ ...values, [f.key]: Number(e.target.value) })}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-brand-400">{f.hint}</p>
            </div>
          ))}
          <button type="button" onClick={handleSave} disabled={busy || !valid} className={btnPrimary}>
            {busy ? t('settings.saving') : t('onboarding.thresholds.save')}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Passo 4: Tour guiado (tutorial interativo pela interface)
// ---------------------------------------------------------------------------

function TourStep({ apply, onNavigate }: StepProps) {
  const t = useT();
  const [tourOpen, setTourOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tourSteps: TourStepDef[] = useMemo(
    () => [
      {
        key: 'intro',
        title: t('onboarding.tour.step.intro.title'),
        body: t('onboarding.tour.step.intro.body'),
      },
      {
        key: 'upload',
        target: 'nav-upload',
        title: t('onboarding.tour.step.upload.title'),
        body: t('onboarding.tour.step.upload.body'),
      },
      {
        key: 'documents',
        target: 'nav-documents',
        title: t('onboarding.tour.step.documents.title'),
        body: t('onboarding.tour.step.documents.body'),
      },
      {
        key: 'catalogo',
        // Decisao de produto: nav-catalogo so existe para super_user; o
        // publico do onboarding sao orgs — o alvo e "Solicitar provedor".
        // Para super_user o tour degrada p/ card centralizado (by design).
        target: 'nav-request-provider',
        title: t('onboarding.tour.step.catalogo.title'),
        body: t('onboarding.tour.step.catalogo.body'),
      },
      {
        key: 'settings',
        target: 'nav-settings',
        title: t('onboarding.tour.step.settings.title'),
        body: t('onboarding.tour.step.settings.body'),
      },
      {
        key: 'done',
        title: t('onboarding.tour.step.done.title'),
        body: t('onboarding.tour.step.done.body'),
      },
    ],
    [t],
  );

  const handleFinish = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      apply(await completeOnboardingStep('tour'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setBusy(false);
      setTourOpen(false);
    }
  }, [apply, t]);

  const handleComplete = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      apply(await completeOnboardingStep('tour'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setBusy(false);
    }
  }, [apply, t]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-brand-900">{t('onboarding.tour.title')}</h2>
        <p className="text-sm text-brand-600">{t('onboarding.tour.subtitle')}</p>
      </div>

      <HelpBox step="tour" onNavigate={onNavigate} />

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setTourOpen(true)}
          disabled={busy}
          className={btnPrimary}
        >
          {t('onboarding.tour.start')}
        </button>
        <button
          type="button"
          onClick={handleComplete}
          disabled={busy}
          className={btnNeutral}
          title={t('onboarding.step.completeHint')}
        >
          {t('onboarding.step.complete')}
        </button>
      </div>

      {tourOpen && (
        <OnboardingTour
          steps={tourSteps}
          onFinish={handleFinish}
          onSkip={() => setTourOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagina: wizard + checklist
// ---------------------------------------------------------------------------

function Onboarding({ onNavigate }: { onNavigate: (v: NavView) => void }) {
  const t = useT();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<OnboardingStepKey>('email');
  const [finishing, setFinishing] = useState(false);

  const reload = useCallback(async () => {
    const s = await fetchOnboarding();
    setState(s);
  }, []);

  useEffect(() => {
    fetchOnboarding()
      .then((s) => {
        setState(s);
        // Abre o wizard no primeiro passo pendente.
        const firstPending = WIZARD_STEPS.find(
          (k) => s.steps.find((x) => x.key === k)?.status !== 'completed',
        );
        if (firstPending) setActive(firstPending);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : t('onboarding.error.load')),
      )
      .finally(() => setLoading(false));
  }, [t]);

  const apply: Apply = useCallback((s: OnboardingState) => {
    setState(s);
  }, []);

  const handleFinish = useCallback(async () => {
    setFinishing(true);
    setError(null);
    try {
      apply(await completeOnboarding());
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.error.action'));
    } finally {
      setFinishing(false);
    }
  }, [apply, t]);

  const idx = WIZARD_STEPS.indexOf(active);
  const allDone = state ? state.progress.percent === 100 : false;
  const completed = state?.status === 'completed';

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-brand-900">{t('onboarding.title')}</h1>
            <p className="text-sm text-brand-600">{t('onboarding.subtitle')}</p>
          </div>
          {state && (
            <div className="min-w-[200px]">
              <div className="mb-1 flex justify-between text-xs text-brand-500">
                <span>{t('onboarding.progress', { done: state.progress.done, total: state.progress.total })}</span>
                <span>{state.progress.percent}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-brand-100">
                <div
                  className="h-full rounded-full bg-brand-600 transition-all"
                  style={{ width: `${state.progress.percent}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {error && <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {loading ? (
          <p className="text-sm text-brand-600">{t('settings.loading')}</p>
        ) : !state ? (
          <p className="text-sm text-brand-500">{t('onboarding.error.load')}</p>
        ) : completed ? (
          // Onboarding concluido — painel de sucesso.
          <div className="rounded-md border border-green-200 bg-green-50 p-6 text-center">
            <h2 className="mb-2 text-lg font-semibold text-green-800">
              {t('onboarding.done.title')}
            </h2>
            <p className="mb-1 text-sm text-green-700">
              {t('onboarding.done.body')}
            </p>
            {(state.parceiros_notified ?? 0) > 0 && (
              <p className="mb-4 text-xs text-green-600">
                {t('onboarding.done.notified', {
                  count: state.parceiros_notified ?? 0,
                })}
              </p>
            )}
            <div className="mt-4 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => onNavigate('upload')}
                className={btnPrimary}
              >
                {t('onboarding.done.goUpload')}
              </button>
              <button
                type="button"
                onClick={() => onNavigate('settings')}
                className={btnNeutral}
              >
                {t('onboarding.done.goSettings')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 sm:flex-row">
            {/* Checklist (progresso visivel) */}
            <aside className="sm:w-64 sm:shrink-0">
              <ol className="space-y-1">
                {WIZARD_STEPS.map((key, i) => {
                  const st = stepOf(state, key);
                  const done = st?.status === 'completed';
                  const isActive = key === active;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => setActive(key)}
                        className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${
                          isActive
                            ? 'bg-brand-100 font-semibold text-brand-900'
                            : 'text-brand-600 hover:bg-brand-50'
                        }`}
                      >
                        <span
                          className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                            done
                              ? 'bg-green-600 text-white'
                              : isActive
                                ? 'bg-brand-700 text-white'
                                : 'bg-brand-200 text-brand-600'
                          }`}
                        >
                          {done ? '✓' : i + 1}
                        </span>
                        <span className="flex-1">{t(`onboarding.step.${key}`)}</span>
                        {done && st?.auto && (
                          <span
                            className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700"
                            title={t('onboarding.step.autoHint')}
                          >
                            {t('onboarding.step.auto')}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ol>
            </aside>

            {/* Painel do passo ativo */}
            <div className="min-w-0 flex-1">
              {active === 'email' && (
                <EmailStep state={state} apply={apply} reload={reload} onNavigate={onNavigate} />
              )}
              {active === 'municipios' && (
                <MunicipiosStep
                  state={state}
                  apply={apply}
                  reload={reload}
                  onNavigate={onNavigate}
                />
              )}
              {active === 'thresholds' && (
                <ThresholdsStep
                  state={state}
                  apply={apply}
                  reload={reload}
                  onNavigate={onNavigate}
                />
              )}
              {active === 'tour' && (
                <TourStep state={state} apply={apply} reload={reload} onNavigate={onNavigate} />
              )}

              {/* Navegacao do wizard */}
              <div className="mt-6 flex items-center justify-between border-t border-brand-100 pt-4">
                <button
                  type="button"
                  onClick={() => idx > 0 && setActive(WIZARD_STEPS[idx - 1])}
                  disabled={idx === 0}
                  className={btnNeutral}
                >
                  {t('onboarding.nav.prev')}
                </button>
                {allDone ? (
                  <button
                    type="button"
                    onClick={handleFinish}
                    disabled={finishing}
                    className={btnPrimary}
                  >
                    {finishing ? t('settings.saving') : t('onboarding.finish')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      idx < WIZARD_STEPS.length - 1 && setActive(WIZARD_STEPS[idx + 1])
                    }
                    disabled={idx === WIZARD_STEPS.length - 1}
                    className={btnPrimary}
                  >
                    {t('onboarding.nav.next')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;
